import { useEffect, useRef, useState } from 'react';
import {
  ABALONE_COORDS,
  ABALONE_DELTAS,
  ABALONE_DIRECTIONS,
  type AbaloneMove,
  type AbaloneState,
  abaloneCellName,
  abaloneIndex,
  abaloneNeighbor,
  abaloneOutcomeText,
  abalonePosition,
  abaloneSelectionMoves,
  describeAbaloneMove,
  isAbaloneStraightGroup,
  type Side,
} from '../../games/abalone';
import './abalone.css';

interface Props {
  state: AbaloneState;
  legal?: AbaloneMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (move: AbaloneMove) => void;
  mini?: boolean;
  lastMove?: AbaloneMove;
  staticView?: boolean;
  suppressTurnStatus?: boolean;
}

const moveKey = (move: AbaloneMove) => `${move.cells.join('-')}-${move.dir}`;

/** One origin controls single marbles and the column extending ahead of that marble. */
function originMoves(legal: readonly AbaloneMove[] | undefined, origin: number | undefined) {
  return origin == null ? [] : (legal ?? []).filter(move => {
    if (!move.cells.includes(origin)) return false;
    if (move.cells.length === 1) return true;
    const first = ABALONE_COORDS[move.cells[0]];
    const last = ABALONE_COORDS[move.cells[move.cells.length - 1]];
    const [dr, dc] = ABALONE_DELTAS[move.dir];
    const inline = (last[0] - first[0]) * dc === (last[1] - first[1]) * dr;
    const behind = abaloneNeighbor(origin, ((move.dir + 3) % 6) as AbaloneMove['dir']);
    return inline && !move.cells.includes(behind ?? -1);
  });
}

/** Every shaft starts at the selected origin, including explicit broadside groups. */
function arrowTarget(move: AbaloneMove, origin: number, flipped: boolean) {
  const first = ABALONE_COORDS[move.cells[0]];
  const last = ABALONE_COORDS[move.cells[move.cells.length - 1]];
  const [dr, dc] = ABALONE_DELTAS[move.dir];
  const inline = move.cells.length === 1 || (last[0] - first[0]) * dc === (last[1] - first[1]) * dr;
  const front = inline ? move.cells.find(cell => !move.cells.includes(abaloneNeighbor(cell, move.dir) ?? -1))! : origin;
  return abalonePosition(abaloneNeighbor(front, move.dir)!, flipped);
}

/** Only a two-cell gap on one of the six hex rays may contribute an own midpoint. */
function withOwnMidpoint(manual: readonly number[], state: AbaloneState): number[] {
  if (manual.length !== 2) return [...manual];
  const [a, b] = manual.map(cell => ABALONE_COORDS[cell]);
  if (!ABALONE_DELTAS.some(([dr, dc]) => b[0] - a[0] === dr * 2 && b[1] - a[1] === dc * 2)) return [...manual];
  const row = (a[0] + b[0]) / 2, col = (a[1] + b[1]) / 2;
  if (!Number.isInteger(row) || !Number.isInteger(col)) return [...manual];
  const middle = abaloneIndex(row, col);
  const own = state.to_move === 'First' ? 1 : 2;
  return middle != null && state.board[middle] === own ? [...manual, middle] : [...manual];
}

/** These are captured opponent balls, never reserves or lost own balls. */
function CaptureTray({ side, count, position }: { side: Side; count: number; position: 'upper' | 'lower' }) {
  const trayColor = side === 'First' ? 'black' : 'white';
  const ballColor = side === 'First' ? 'white' : 'black';
  return <div className={`abalone-capture-tray ${trayColor} ${position}`}
    data-testid={`abalone-capture-${position}`} data-owner={side} data-captured={count}
    role="img" aria-label={`${side === 'First' ? '黒' : '白'}が捕獲した${side === 'First' ? '白' : '黒'}玉 ${count} / 6`}>
    <span className="sr-only" aria-hidden data-testid={`abalone-score-${side === 'First' ? 'first' : 'second'}`}>{count}</span>
    {Array.from({ length: 6 }, (_, index) => <span key={index} className="abalone-capture-slot" aria-hidden>
      {index < count && <i className={`abalone-captured-marble ${ballColor}`} />}
    </span>)}
  </div>;
}

export function AbaloneBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  suppressTurnStatus = false,
}: Props) {
  const isMyTurn = myTurnAllowed ?? (yourSide != null && yourSide === state.to_move);
  const interactive = !mini && !staticView && isMyTurn && state.outcome == null && onMove != null;
  const flipped = yourSide === 'Second';
  const lowerSide: Side = yourSide === 'Second' ? 'Second' : 'First';
  const upperSide: Side = lowerSide === 'First' ? 'Second' : 'First';
  const capturedBy = (side: Side) => side === 'First' ? state.first_pushed_off : state.second_pushed_off;
  const ownPiece = state.to_move === 'First' ? 1 : 2;
  // Reset synchronously when the position changes, including replay navigation.
  const positionKey = `${state.ply}/${state.to_move}/${state.board.join('')}/${state.outcome?.kind ?? ''}`;
  const [selection, setSelection] = useState({ positionKey, manual: [] as number[], hover: '' });
  const manualCells = selection.positionKey === positionKey && interactive ? selection.manual : [];
  const selectedCells = withOwnMidpoint(manualCells, state);
  const origin = manualCells[0];
  const lastSelected = manualCells.at(-1);
  const groupMode = selectedCells.length > 1;
  const selectionMoves = groupMode ? abaloneSelectionMoves(legal, selectedCells) : originMoves(legal, origin);
  const preview = selectionMoves.find(move => moveKey(move) === selection.hover);
  const boardRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 320, height: 320 / 1.12 });
  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(board);
    return () => observer.disconnect();
  }, []);
  const targetSize = Math.max(24, Math.min(40, size.width * 0.088));
  const arrows = origin == null ? [] : selectionMoves.map(move => {
    const start = abalonePosition(origin, flipped);
    const target = arrowTarget(move, origin, flipped);
    const x1 = start.left * size.width / 100, y1 = start.top * size.height / 100;
    const x2 = target.left * size.width / 100, y2 = target.top * size.height / 100;
    const length = Math.hypot(x2 - x1, y2 - y1);
    const ux = (x2 - x1) / length, uy = (y2 - y1) / length;
    const head = Math.min(8, targetSize / 4);
    const path = `M${x1} ${y1}L${x2} ${y2}M${x2 - ux * head - uy * head * 0.65} ${y2 - uy * head + ux * head * 0.65}L${x2} ${y2}L${x2 - ux * head + uy * head * 0.65} ${y2 - uy * head - ux * head * 0.65}`;
    return { move, start, target, path };
  });
  const lastCells = new Set<number>();
  if (lastMove) {
    for (const cell of lastMove.cells) {
      lastCells.add(cell);
      const next = abaloneNeighbor(cell, lastMove.dir);
      if (next !== null) lastCells.add(next);
    }
  }

  function selectCell(cell: number) {
    if (!interactive || state.board[cell] !== ownPiece) return;
    let manual: number[];
    if (selectedCells.includes(cell)) {
      // Retapping an automatically included middle clears the group: retaining both
      // endpoints would immediately re-add that middle and make deselection impossible.
      const remaining = manualCells.filter(selected => selected !== cell);
      manual = !manualCells.includes(cell) || withOwnMidpoint(remaining, state).includes(cell) ? [] : remaining;
    } else {
      const proposed = [...manualCells, cell];
      const group = withOwnMidpoint(proposed, state);
      manual = proposed.length <= 3 && isAbaloneStraightGroup(group) ? proposed : [cell];
    }
    setSelection({ positionKey, manual, hover: '' });
  }

  const blocked = origin != null && selectionMoves.length === 0;
  const hint = blocked ? 'ここから動けません' : '玉を選び、矢印で移動。';

  return (
    <div className={`abalone-frame${mini ? ' mini' : ''}`} data-testid="abalone-frame">
      {!mini && (
        <div className="abalone-score" data-testid="abalone-score">
          {!suppressTurnStatus && <div className={`abalone-turn${state.outcome ? '' : ' sr-only'}`} data-testid="abalone-turn">
            {state.outcome ? abaloneOutcomeText(state.outcome) : `${state.to_move === 'First' ? '黒' : '白'}の手番`}
          </div>}
          {interactive && <p className={`abalone-hint${blocked ? '' : ' sr-only'}`} role="status" data-testid="abalone-hint">{hint}</p>}
        </div>
      )}
      <div className="abalone-board-wrap">
      {!mini && <>
        <CaptureTray side={upperSide} count={capturedBy(upperSide)} position="upper" />
        <CaptureTray side={lowerSide} count={capturedBy(lowerSide)} position="lower" />
      </>}
      <div
        ref={boardRef}
        className={`abalone-board${interactive ? ' interactive' : ''}`}
        data-testid="abalone-board"
        data-ply={state.ply}
        data-side={state.to_move}
        data-selected={selectedCells.length}
        data-origin={origin ?? ''}
        data-mode={groupMode ? 'group' : 'origin'}
        data-last-selected={lastSelected ?? ''}
        data-manual-selected={manualCells.length}
        data-auto-selected={selectedCells.length - manualCells.length}
        data-candidates={selectionMoves.length}
        data-preview={preview ? moveKey(preview) : ''}
        aria-label="アバロンの六角盤"
      >
        <div className="abalone-surface" aria-hidden />
        {!mini && (
          <div className="abalone-coords" data-testid="abalone-coords" aria-hidden>
            {Array.from({ length: 9 }, (_, row) => {
              const index = ABALONE_COORDS.findIndex(([r, c]) => r === row && c === Math.max(0, row - 4));
              const p = abalonePosition(index, flipped);
              return <span key={`row-${row}`} data-axis="row" style={{ left: `${p.left + (flipped ? 15 : -15)}%`, top: `${p.top}%` }}>{String.fromCharCode(65 + row)}</span>;
            })}
            {Array.from({ length: 9 }, (_, col) => {
              const index = ABALONE_COORDS.findIndex(([r, c]) => r === Math.max(0, col - 4) && c === col);
              const p = abalonePosition(index, flipped);
              return <span key={`col-${col}`} data-axis="col" style={{ left: `${p.left + (flipped ? -6 : 6)}%`, top: `${p.top + (flipped ? -12 : 12)}%` }}>{col + 1}</span>;
            })}
          </div>
        )}
        {preview && preview.cells.length > 1 && (
          <svg className="abalone-group" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
            {preview.cells.slice(1).map((cell, i) => {
              const a = abalonePosition(preview.cells[i], flipped);
              const b = abalonePosition(cell, flipped);
              return <line key={cell} x1={a.left} y1={a.top} x2={b.left} y2={b.top} />;
            })}
          </svg>
        )}
        {ABALONE_COORDS.map((_, index) => {
          const piece = state.board[index] ?? 0;
          const selected = selectedCells.includes(index);
          const position = abalonePosition(index, flipped);
          const name = abaloneCellName(index);
          return (
            <button
              type="button"
              key={index}
              className={`abalone-cell ${piece === 1 ? 'black' : piece === 2 ? 'white' : 'empty'}${selected ? ' selected' : ''}${lastSelected === index ? ' last-selected' : ''}${preview?.cells.includes(index) ? ' preview-group' : ''}${!mini && lastCells.has(index) ? ' last-move' : ''}`}
              style={{ left: `${position.left}%`, top: `${position.top}%` }}
              onClick={() => selectCell(index)}
              disabled={!interactive || piece !== ownPiece}
              aria-label={`${name} ${piece === 1 ? '黒玉' : piece === 2 ? '白玉' : '空き'}`}
              aria-pressed={selected}
              title={name}
              data-testid={`abalone-cell-${index}`}
              data-coordinate={name}
              data-piece={piece}
            >
              {piece !== 0 && <span className="abalone-marble" aria-hidden />}
            </button>
          );
        })}
        {preview?.cells.map(cell => {
          const p = abalonePosition(abaloneNeighbor(cell, preview.dir)!, flipped);
          return <span key={`target-${cell}`} className="abalone-preview-target" style={{ left: `${p.left}%`, top: `${p.top}%` }} aria-hidden />;
        })}
        {interactive && origin != null && (
          <div className="abalone-directions" aria-label="選んだ始点からの合法方向">
            <svg className="abalone-arrow-lines" viewBox={`0 0 ${size.width} ${size.height}`} aria-hidden>
              {arrows.map(({ move, start, target, path }) => (
                <path key={moveKey(move)} d={path} className={preview === move ? ' preview' : ''}
                  data-testid={`abalone-arrow-${move.dir}-${move.cells.join('-')}`}
                  data-origin={origin} data-start={`${start.left},${start.top}`} data-end={`${target.left},${target.top}`} />
              ))}
            </svg>
            {arrows.map(({ move, target }) => {
              const name = ABALONE_DIRECTIONS[move.dir];
              const visualDir = (move.dir + (flipped ? 3 : 0)) % 6;
              const description = describeAbaloneMove(state, move);
              return (
                <button type="button" key={moveKey(move)} className="abalone-direction"
                  style={{ left: `${target.left}%`, top: `${target.top}%`, width: targetSize, height: targetSize }}
                  aria-label={`${move.cells.map(abaloneCellName).join('・')} ${move.cells.length}玉 ${name} ${description} 移動`}
                  title={description} data-testid={`abalone-dir-${move.dir}-${move.cells.join('-')}`}
                  data-dir={move.dir} data-cells={move.cells.join(',')} data-count={move.cells.length}
                  data-origin={origin} data-legal="true" data-visual-dir={visualDir}
                  onPointerEnter={event => {
                    if (event.pointerType === 'mouse') setSelection(previous => ({ ...previous, hover: moveKey(move) }));
                  }}
                  onPointerLeave={() => setSelection(previous => ({ ...previous, hover: '' }))}
                  onFocus={() => setSelection(previous => ({ ...previous, hover: moveKey(move) }))}
                  onBlur={() => setSelection(previous => ({ ...previous, hover: '' }))}
                  onClick={() => {
                    setSelection({ positionKey, manual: [], hover: '' });
                    onMove?.(move);
                  }}
                >
                  <span className="abalone-candidate-count" aria-hidden>{move.cells.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      </div>

    </div>
  );
}
